import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk, RootState } from '../../../store';
import type { Tenant } from '../../../api/organizations';
import type { GlobalTokens, TenantSession } from '../../../utils/tenantSessionStorage';
import {
  loadActiveTenantIdFromStorage,
  loadTenantSessionsFromStorage,
  saveActiveTenantIdToStorage,
  saveTenantSessionsToStorage,
} from '../../../utils/tenantSessionStorage';
import { deletePushToken } from '../../../api/pushTokens';
import { localStorageGet } from '../../../utils/storage';
import isElectron from 'is-electron';
import { notifyTenantSessionRemoved } from '../../../components/AppToast/notifyTenantSessionRemoved';
import { isMultiOrgFeatureEnabled } from '../../../utils/features';
import { findSessionsWithIndicator } from './indicators';

export type { TenantSession } from '../../../utils/tenantSessionStorage';

type OrganizationsState = {
  tenant: Tenant | null;
  sessions: TenantSession[];
  activeTenantId: number | null;
};

const initialOrganizationsState: OrganizationsState = {
  tenant: null,
  // Rehydrate before route loaders run.
  sessions: loadTenantSessionsFromStorage(),
  activeTenantId: loadActiveTenantIdFromStorage(),
};

const organizationsSlice = createSlice({
  name: 'organizations',
  initialState: initialOrganizationsState,
  reducers: {
    setTenant: (state, action: PayloadAction<Tenant>) => {
      state.tenant = action.payload;
    },
    clearTenant: state => {
      state.tenant = null;
    },
    setTenantSession: (state, action: PayloadAction<TenantSession>) => {
      const session = action.payload;
      const idx = state.sessions.findIndex(s => s.tenantId === session.tenantId);
      if (idx === -1) {
        state.sessions.push(session);
      } else {
        state.sessions[idx] = session;
      }
    },
    removeTenantSession: (state, action: PayloadAction<number | string>) => {
      const tenantId = Number(action.payload);
      state.sessions = state.sessions.filter(s => s.tenantId !== tenantId);
      if (state.activeTenantId != null && state.activeTenantId === tenantId) {
        state.activeTenantId = null;
      }
    },
    setActiveTenantId: (state, action: PayloadAction<number | null>) => {
      state.activeTenantId = action.payload;
    },
    updateTenantSessionMetadata: (
      state,
      action: PayloadAction<{
        tenantId: number;
        metadata: Partial<Pick<TenantSession, 'tenantName' | 'tenantLogo' | 'email'>>;
      }>
    ) => {
      const { tenantId, metadata } = action.payload;
      const existing = state.sessions.find(s => s.tenantId === tenantId);
      if (!existing) return;
      existing.tenantName = metadata.tenantName ?? existing.tenantName;
      existing.tenantLogo =
        metadata.tenantLogo === undefined ? existing.tenantLogo : metadata.tenantLogo;
      existing.email = metadata.email ?? existing.email;
    },
    updateTenantSessionTokens: (
      state,
      action: PayloadAction<{ tenantId: number; tokens: GlobalTokens }>
    ) => {
      const { tenantId, tokens } = action.payload;
      const existing = state.sessions.find(s => s.tenantId === tenantId);
      if (!existing) return;
      Object.assign(existing, tokens);
    },
    resetTenantSessions: state => {
      state.sessions = [];
      state.activeTenantId = null;
    },
    setSessionAlarm: (state, action: PayloadAction<{ tenantId: number; value: boolean }>) => {
      const { tenantId, value } = action.payload;
      const existing = state.sessions.find(s => s.tenantId === tenantId);
      if (!existing) return;
      existing.hasActiveAlarm = value;
    },
    setSessionIndicators: (
      state,
      action: PayloadAction<{
        tenantId: number;
        hasActiveAlarm: boolean;
        hasUnreadMessages: boolean;
        hasActiveImOk: boolean;
      }>
    ) => {
      const { tenantId, hasActiveAlarm, hasUnreadMessages, hasActiveImOk } = action.payload;
      const existing = state.sessions.find(s => s.tenantId === tenantId);
      if (!existing) return;
      existing.hasActiveAlarm = hasActiveAlarm;
      existing.hasUnreadMessages = hasUnreadMessages;
      existing.hasActiveImOk = hasActiveImOk;
    },
  },
});

export const {
  setTenant,
  clearTenant,
  setTenantSession,
  removeTenantSession,
  setActiveTenantId,
  updateTenantSessionMetadata,
  updateTenantSessionTokens,
  resetTenantSessions,
  setSessionAlarm,
  setSessionIndicators,
} = organizationsSlice.actions;

export const selectTenant = (state: RootState) => state.organizations.tenant;

/**
 * ID of the currently loaded tenant *row* (i.e. `state.organizations.tenant.id`).
 * Prefer `selectActiveTenantId` for the registry pointer — the two agree in
 * steady state but diverge briefly while switching orgs (globals flipped, row
 * not yet refetched).
 */
export const selectTenantId = (state: RootState) => {
  return selectTenant(state)?.id;
};

export const selectAllTenantSessions = (state: RootState) => state.organizations.sessions;

/** True when there are two or more tenant sessions (i.e. the user has switched org at least once). */
export const selectHasMultipleSessions = createSelector(
  [selectAllTenantSessions],
  sessions => sessions.length > 1
);

export const selectActiveTenantId = (state: RootState) => state.organizations.activeTenantId;

export const selectActiveTenantSession = (state: RootState) => {
  const id = state.organizations.activeTenantId;
  return id != null ? (state.organizations.sessions.find(s => s.tenantId === id) ?? null) : null;
};

export const selectTenantSessionById = (tenantId: number | string) => (state: RootState) =>
  state.organizations.sessions.find(s => s.tenantId === Number(tenantId)) ?? null;

/**
 * Inactive sessions with any indicator, sorted by indicator priority. Priority
 * order is owned by `findSessionsWithIndicator` — see `./indicators.ts`.
 */
export const selectInactiveSessionsWithIndicator = createSelector(
  [selectAllTenantSessions, selectActiveTenantId],
  (sessions, activeId) => findSessionsWithIndicator(sessions.filter(s => s.tenantId !== activeId))
);

export const selectInactiveAlarmCount = createSelector(
  [selectAllTenantSessions, selectActiveTenantId],
  (sessions, activeId) => sessions.filter(s => s.tenantId !== activeId && s.hasActiveAlarm).length
);

export function persistTenantSessions(state: RootState): void {
  saveTenantSessionsToStorage(selectAllTenantSessions(state));
}

export const pruneTenantSession =
  ({ tenantId, alertUser }: { tenantId: number; alertUser: boolean }): AppThunk<void> =>
  (dispatch, getState) => {
    const session = selectTenantSessionById(tenantId)(getState());
    if (alertUser) {
      notifyTenantSessionRemoved(session?.tenantName);
    }
    dispatch(removeTenantSession(tenantId));

    const pushToken = isElectron() ? localStorageGet('pushyToken') : null;
    if (pushToken && session) {
      deletePushToken(pushToken, session.userId).catch(() => {});
    }

    persistTenantSessions(getState());
  };

/**
 * Sets the active tenant and, in multi-org, syncs `activeTenantId` + storage.
 * Callers always pass the single tenant they want active.
 */
export const applyActiveTenantFromList =
  (tenant: Tenant): AppThunk<Tenant> =>
  dispatch => {
    dispatch(setTenant(tenant));
    if (isMultiOrgFeatureEnabled()) {
      dispatch(setActiveTenantId(tenant.id));
      saveActiveTenantIdToStorage(tenant.id);
    }
    return tenant;
  };

export default organizationsSlice.reducer;
